import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'IFS Cloud & Core',
    items: [
      { name: 'IFS Cloud Functional Consultant', icon: '☁️' },
      { name: 'IFS Applications 10', icon: '📦' },
      { name: 'IFS Cloud Architect', icon: '🏗️' },
      { name: 'IFS Technical Developer', icon: '💻' },
    ]
  },
  {
    title: 'Industry & Operations',
    items: [
      { name: 'IFS Aerospace & Defense', icon: '✈️' },
      { name: 'IFS Manufacturing', icon: '🏭' },
      { name: 'IFS Energy & Utilities', icon: '⚡' },
      { name: 'IFS Project Management', icon: '📋' },
    ]
  },
  {
    title: 'Modules & Services',
    items: [
      { name: 'IFS Field Service Mgmt (FSM)', icon: '🔧' },
      { name: 'IFS Finance & Controlling', icon: '💰' },
      { name: 'IFS Supply Chain / Procurement', icon: '🔗' },
      { name: 'IFS HR / Payroll', icon: '👥' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Genuine IFS Platform Expertise',
    text: 'Our recruiters understand IFS at a technical and functional level — IFS Cloud architecture, Aurena, IFS FSM, Lobby configuration, and IFS component framework. We assess real competency, not keyword matches.',
  },
  {
    icon: '✔',
    title: 'Thorough IFS-Specific Vetting',
    text: 'All IFS consultants are assessed for: IFS Cloud or IFS Applications 10 implementation experience, FSM configuration and deployment expertise, industry-specific knowledge (A&D, manufacturing, energy), and IFS technical skills (PL/SQL, IFS components, Aurena).',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Staff augmentation, project delivery, or permanent hire. From a single IFS consultant to a full implementation team — we adapt to your program structure and budget.',
  },
  {
    icon: '🛡',
    title: 'IFS Cloud Migration & Upgrade Expertise',
    text: 'We source consultants with hands-on experience in IFS Applications to IFS Cloud migrations — minimizing technical and operational risk on your modernization programs.',
  },
];

const problemItems = [
  'Implementation failures from consultants unfamiliar with IFS component-based architecture and event-driven design.',
  'FSM configuration errors due to lack of hands-on IFS Field Service Management deployment experience.',
  'Costly IFS Cloud migration delays from consultants without IFS Cloud or Aurena UI/UX expertise.',
  'Poor system adoption when configuration doesn\'t align to your industry\'s operational workflows.',
  'Extended support costs from IFS environments that were never properly configured or documented.',
];

const solutionPoints = [
  'IFS-specialist recruiters who understand the IFS product family',
  'Verified real IFS project experience — not just platform familiarity',
  'Certified IFS talent matched to your product version, industry vertical, and project methodology',
];

const outcomes = [
  {
    title: 'Successful IFS Go-Lives',
    text: 'Certified IFS professionals who deliver on time and within the project scope.',
  },
  {
    title: 'Industry-Aligned Configuration',
    text: 'Consultants who map IFS capabilities to your operational processes and vertical workflows.',
  },
  {
    title: 'Smooth IFS Cloud Transitions',
    text: 'Specialists who reduce disruption during version upgrades or IFS Cloud migrations.',
  },
  {
    title: 'Higher IFS Platform Adoption',
    text: 'Experts who configure IFS to drive real user adoption and business value.',
  },
  {
    title: 'Reduced IFS Talent Risk',
    text: 'Pre-vetted professionals from a specialized, hard-to-find talent community.',
  },
];

const IFSStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>IFS Staffing India | IFS Cloud & ERP Recruitment | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides certified IFS staffing solutions. Hire pre-vetted IFS Cloud consultants, IFS FSM specialists, IFS Applications 10 experts, and IFS technical developers for your ERP programs."
        />
        <meta
          name="keywords"
          content="IFS staffing India, IFS Cloud recruitment, IFS consultants India, IFS Applications 10 staffing, IFS FSM specialists, IFS ERP staffing, IFS Cloud migration experts, IFS talent acquisition, IFS developer recruitment, IFS aerospace defense staffing, IFS manufacturing consultants, IFS energy utilities, Kapil Technologies IFS"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="IFS Staffing India | IFS Cloud & ERP Recruitment | Kapil Technologies"
        />
        <meta
          property="og:description"
          content="Kapil Technologies provides certified IFS staffing solutions. Hire pre-vetted IFS Cloud consultants and IFS ERP specialists."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="IFS Staffing India | IFS Cloud & ERP Recruitment"
        />
        <meta
          name="twitter:description"
          content="Certified IFS staffing solutions. Hire pre-vetted IFS Cloud consultants and IFS ERP specialists."
        />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/ifs"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602811/Kapil_Tech_IFS_Staffing_Page_luwu70.mp4"
        titleWhite="Certified IFS Cloud & IFS Applications Consultants."
        titleGradient="Industry-Specific ERP Talent."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Looking for Experienced IFS Consultants for Your ERP Program?"
        description="Kapil Tech connects enterprises with pre-vetted IFS professionals across IFS Cloud, IFS Applications 10, Field Service Management, and industry-specific implementations. In a market where genuine IFS expertise is rare, our specialist practice delivers qualified consultants — fast."
        stats={[
          { value: "120+", label: "IFS Experts Placed" },
          { value: "95%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_IFS_Staffing_Page.jpg?updatedAt=1781603014113"
        imageAlt="IFS Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="IFS Products & Roles We Staff"
        description="Our IFS talent network covers every major product, module, and industry specialization in the IFS ecosystem."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Organizations Choose Us */}
      <TechStaffingServices 
        title="Why Organizations Choose Us for IFS Staffing"
        description="We combine deep IFS domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the Scarcity of IFS Talent Blocking Your ERP Delivery?"
        description="IFS is a powerful but specialized ERP platform with a small certified talent community. Most recruiters lack the knowledge to assess IFS expertise beyond a keyword match — leading to placements that cannot navigate IFS Cloud architecture, FSM configuration, or IFS-specific customization frameworks."
        problemTitle="The Real Cost of a Wrong IFS Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech Solution"
        solutionDescription="Kapil Tech operates exclusively within the technology talent ecosystem, with a focused IFS practice staffed by recruiters who understand the platform's unique architecture and requirements."
        solutionPoints={solutionPoints}
        ctaText="Find Your IFS Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your IFS initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="IFS Experts?"
        description="Share your IFS staffing requirements today. Our team responds within hours with pre-vetted candidates matched to your version and industry."
      />
    </div>
  );
};

export default IFSStaffHome;
